import { CalendarMonth } from "@tento-ui/components/calendar-month/calendar-month";
import { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import { CalendarMonthSpan } from "@tento-ui/components/calendar-month/calendar-month-span";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import { type Option } from "@tento-lona/sheets";
import { NaiveDate, TimezoneRegion } from "@tento-chrono";
import { calculateStartPadding, dateToUtcDayRange } from "@tento-lona/sheets-ui/plugins/layout/grid/month/calendar-month-utils.ts";
import { AllDaySpan } from "@tento-lona/sheets-ui/plugins/layout/grid/month/all-day-span.ts";
import { CalendarPreferences } from "@tento-lona";
import type { CalendarMonthCell } from "@tento-ui/components/calendar-month/calendar-month-cell";
import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import { EventListItems } from "./build-event-list-rows.ts";
import { EventId, EventItem, EventItemAllDay, EventItemTimed } from "@tento-lona/sheets";
import { EventAccess } from "@tento-lona/sheets-ui/sheet/gestures/event-access.ts";
import type { MonthActionsContext } from "./month-actions-context.ts";

/**
 * What the month grid paints for events: the all-day spans (and the pending
 * new-event span), the per-cell entry lists with their drag wiring, and the
 * ghost span an entry row drags.
 */
export class MonthSpanBuilder {
  readonly #ctx: MonthActionsContext;

  constructor(ctx: MonthActionsContext) {
    this.#ctx = ctx;
  }

  get #prefs(): CalendarPreferences {
    return this.#ctx.coordinator.eventDetailsManager.calendarPreferences;
  }


  buildAllSpans(): CalendarMonth.Span[] {
    const month = this.#ctx.delegate.getMonth();
    if (month == null) return [];

    const today = NaiveDate.today();
    // Calculate grid range (same logic as bind)
    const firstDay = NaiveDate.fromYmd1(month.yr, month.mth, 1).exp();
    const gridStart = firstDay.addDays(
      -calculateStartPadding(firstDay.dayOfWeek, this.#prefs.startOfWeek()),
    );
    const gridEnd = gridStart.addDays(41);
    const monthRange = new NaiveDate.Range(gridStart, gridEnd);
    // Render hot path — if the facade isn't reachable yet (no bound
    // row or no event-capable descendant), render without events. The
    // invalidation subscriber re-binds once events ingest.
    //
    // The facade returns sheets-side EventItem; we wrap each to
    // SDK-side `EventItem` so downstream paths (`createAllDaySpan`)
    // can use the SDK-side API. Pattern from
    // `calendar-list.ts:357` (proven in production).
    const fullDayEvents = this.#ctx.eventsSource
      .peekAllDayInRange(monthRange)
      .map((e) => EventItem.of(e.inner, e.origin))
      .filter((e): e is EventItemAllDay => e instanceof EventItemAllDay);
    const allDaySpans: CalendarMonth.Span[] = fullDayEvents.map((e) => {
      const span = AllDaySpan.make(e, today, this.#ctx.sdkEvents, this.#ctx.accessOf(e));
      const render = span.render;
      return {
        ...span,
        render: (info, callbacks) => {
          const $span = render(info, callbacks);
          this.#ctx.eventActiveState.bind(e.id, $span);
          return $span;
        },
      };
    });
    const pendingSpan = this.buildPendingSpan();

    return [...allDaySpans, pendingSpan].filter(
      (s): s is CalendarMonth.Span => s != null,
    );
  }

  buildPendingSpan(): Option<CalendarMonth.Span> {
    const pendingEvent = this.#ctx.delegate.getPendingEvent();
    if (!pendingEvent) return null;
    return {
      key: "pending-event",
      contentSig: [
        "pending",
        pendingEvent.dateRange.start.toString(),
        pendingEvent.dateRange.end.toString(),
        pendingEvent.title ?? "",
        pendingEvent.category ?? "",
        pendingEvent.backgroundColor ?? "",
      ].join("|"),
      range: pendingEvent.dateRange,
      // isEditable: false prevents CalendarMonthExistingGestures.MoveSpan from interfering
      // CalendarMonthNewGestures.Move identifies pending span by date range match instead
      isEditable: false,
      isPending: true,
      render: (info, callbacks) => {
        const categoryColor =
          this.#ctx.sdkEvents.categories.itemOpt(pendingEvent.category ?? null)?.colorOpt() ?? null;
        const $span = CalendarMonthSpan.makeWith(
          {
            title: pendingEvent.title ?? "New Event",
            color: pendingEvent.backgroundColor ?? categoryColor ??
              "var(--blue)",
            segment: info,
            showResizeHandles: true,
          },
          callbacks,
        );
        $span.style.opacity = "0.6";
        $span.dataset.pending = "true";
        return $span;
      },
    };
  }

  /**
   * Paint the per-cell event-list content for a calendar-month day cell.
   *
   * Loads events for the cell's UTC day range, filters out full-day
   * events (those are spans), wraps sheets-side `EventItem` → SDK-side,
   * builds the `EntryList` widget, wires gestures, and appends to
   * `$cell`. Layout caller is responsible for the stale-cell check via
   * the `isStillValid` callback (returns true if the cell hasn't been
   * re-bound since the layout enqueued this call).
   *
   * Phase F.6 — moves the event-list rendering out of
   * `CalendarMonthHost.bindCell` so the layout file no longer touches
   * `EventItem` / `EventsPlatform` / event domain types.
   */
  async bindEventCellContent(
    $cell: CalendarMonthCell,
    date: NaiveDate,
    tz: TimezoneRegion,
    gestureManager: CalendarMonthGesture.Manager<CalendarMonthGesture.Info>,
    isStillValid: () => boolean,
  ): Promise<void> {
    const range = dateToUtcDayRange(date, tz);
    await this.#ctx.eventsSource.ensureLoaded(range);
    if (!isStillValid()) return;
    this.buildEventCellContentNow($cell, date, tz, gestureManager);
  }

  /**
   * Synchronous sibling of `bindEventCellContent`: renders the cell's
   * partial-day event list from **already-loaded** data (no
   * `ensureEventsLoaded`). The layout's `bindCell` calls this directly so
   * the cell paints in the same frame as the layout bind — no
   * empty-then-fill flicker when the data is already cached. Range
   * loading is driven once per bind by `provideSpans` at the layout
   * level; when that lands it fires the same `"calendars.events"`
   * invalidation that triggers a re-bind, which re-seeds the cells from
   * the now-loaded data.
   *
   * Mirrors `provideSpansNow` (the span seed) at the cell-content level.
   */
  buildEventCellContentNow(
    $cell: CalendarMonthCell,
    date: NaiveDate,
    tz: TimezoneRegion,
    gestureManager: CalendarMonthGesture.Manager<CalendarMonthGesture.Info>,
  ): void {
    const range = dateToUtcDayRange(date, tz);
    // Filter out all-day events since they're now rendered as spans.
    // Wrap sheets-side facade items to SDK-side `EventItem` so the
    // downstream `event.rowProps(...)` call works.
    const events = this.#ctx.eventsSource
      .peekInRange(range)
      .map((e) => EventItem.of(e.inner, e.origin))
      .filter((e): e is EventItemTimed =>
        e instanceof EventItemTimed,
      )
      .slice(0, 5);
    const timeFormat = this.#ctx.calendarPreferences.peek().timeFormat;
    const dateFormat = this.#ctx.calendarPreferences.peek().dateFormat;
    const built = EventListItems.build({
      events,
      eventsPlatform: this.#ctx.eventsPlatform,
      tz,
      calendars: this.#ctx.calendars,
      timeFormat,
      dateFormat,
      presentation: this.#ctx.sdkEvents,
      list: this.#ctx.calendarListAccess(),
    });
    const items = MonthSpanBuilder.entryRowsFor(built, events, (event) => this.#ctx.accessOf(event));
    const $e = EntryList.makeWith({
      items,
      display: { timeScale: "day", use24Hour: timeFormat === "24h", dateFormat },
      onRowBound: (item, $row) => {
        const eventId = EventId.parse(item.id ?? "");
        if (eventId == null) return;
        this.#ctx.eventActiveState.bind(eventId, $row);
      },
    });
    $e.setAttribute("type", "cell");

    // Wire up gesture callbacks for drag support
    $e.bindCallbacks(
      gestureManager.asEntryListDelegate(
        { date, toString: () => date.toString() },
        items,
        (item, ghostDate) => this.createEventGhostSpan(item, ghostDate),
      ),
    );

    $cell.appendChild($e);
  }

  /**
   * The month cell's entry rows as the user may drag them (MoveEntryRow
   * stops at an `isEditable: false` row: no ghost, no claim): only the rows
   * of events whose `accessOf(event).move` holds keep their drag. Answers
   * `built` itself when no row changes.
   */
  static entryRowsFor(
    built: ListItem[],
    events: readonly EventItem[],
    accessOf: (event: EventItem) => EventAccess,
  ): ListItem[] {
    const fixed = new Set(events.filter((event) => !accessOf(event).move).map((event) => event.id.toString()));
    const loses = (item: ListItem): boolean => item.id != null && fixed.has(item.id) && item.isEditable !== false;
    if (!built.some(loses)) return built;
    return built.map((item) => loses(item) ? { ...item, isEditable: false } : item);
  }


  createEventGhostSpan(item: ListItem, date: NaiveDate): CalendarMonth.Span {
    return {
      key: "cell-event-ghost",
      contentSig: `cell-event-ghost|${item.title}|${item.accentColor ?? "var(--gray)"}|${date.toString()}`,
      range: new NaiveDate.Range(date, date),
      isEditable: item.isEditable ?? false,
      render: (info) => {
        const $span = CalendarMonthSpan.makeWith({
          title: item.title,
          color: item.accentColor ?? "var(--gray)",
          segment: info,
        });
        $span.style.opacity = "0.7";
        return $span;
      },
    };
  }
}
